import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Paper,
  IconButton,
  Stack,
  Skeleton,
  Tooltip
} from '@mui/material';
import { PhotoCamera, DeleteOutline, CheckCircleOutline } from '@mui/icons-material';
import palette from 'src/theme/palette';

const ImageShimmerLoader = () => (
  <Card variant="outlined" sx={{ position: 'relative' }}>
    <Box sx={{ p: 1 }}>
      <Skeleton variant="rectangular" width="100%" height={160} sx={{ borderRadius: 1 }} />
    </Box>
    <CardContent sx={{ pt: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Skeleton variant="text" width="70%" height={20} />
        <Stack direction="row" spacing={0.5}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={32} height={32} />
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

const PropertyImagesTab = ({
  form,
  uploadingImages,
  handleImageUpload,
  setDefaultImage,
  removeImage,
  loadingProperty
}) => {
  const totalImages = form.images.length + uploadingImages.length;
  const isMaxLimitReached = totalImages >= 50;
  const isUploadDisabled = loadingProperty || isMaxLimitReached;
  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Upload Property Images 
      </Typography>
      <input
        accept="image/*"
        id="images-upload"
        multiple
        type="file"
        style={{ display: 'none' }}
        onChange={handleImageUpload}
        disabled={isUploadDisabled}
      />
      <label htmlFor="images-upload">
        <Button
          variant="contained"
          component="span"
          startIcon={<PhotoCamera />}
          sx={{
            background: isUploadDisabled ? palette.grey[400] : palette.primary.main,
            '&:hover': {
              background: isUploadDisabled ? palette.grey[400] : palette.primary.dark
            }
          }}
          disabled={isUploadDisabled}
        >
          {isMaxLimitReached ? 'Maximum 50 Images Reached' : 'Upload Images'}
        </Button>
      </label>
      {isMaxLimitReached && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Maximum limit of 50 images reached. Remove some images to upload new ones.
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mt: 2 }}>
        {uploadingImages.map((img, i) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={`uploading-${i}`}>
            <ImageShimmerLoader />
          </Grid>
        ))}

        {form.images.length === 0 && uploadingImages.length === 0 && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2">
                {loadingProperty ? 'Loading images...' : 'No images uploaded yet.'}
              </Typography>
            </Paper>
          </Grid>
        )}

        {form.images.map((img, i) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
            <Card variant="outlined" sx={{ position: 'relative' }}>
              <Box sx={{ p: 1 }}>
                <a
                  href={img.preview || img.serverUrl}
                  data-fancybox="property-gallery"
                  data-caption={img.name}
                >
                  <Box
                    component="img"
                    src={img.preview || img.serverUrl}
                    alt={img.name}
                    sx={{
                      width: '100%',
                      height: 160,
                      objectFit: 'cover',
                      borderRadius: 1,
                      opacity: img.uploadError ? 0.5 : 1,
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.05)'
                      }
                    }}
                  />
                </a>
                {img.uploadError && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: palette.error.main,
                      color: 'white',
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                      fontSize: '12px'
                    }}
                  >
                    Upload Failed
                  </Box>
                )}
              </Box>
              <CardContent sx={{ pt: 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                    {img.name}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Set as default">
                      <IconButton
                        size="small"
                        color={img.isDefault ? 'primary' : 'default'}
                        onClick={() => setDefaultImage(i)}
                        disabled={img.uploadError || loadingProperty}
                      >
                        <CheckCircleOutline />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        onClick={() => removeImage(i)}
                        disabled={loadingProperty}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PropertyImagesTab;
